import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import MiniLeaderboard from '../common/MiniLeaderboard';
import PersonaBadge from '../common/PersonaBadge';
import { EloRating, Match } from '../../types/gymnasium';
import { FiArrowRight } from 'react-icons/fi';

interface GymnasiumSpotlightProps {
  /** Pre-fetched Elo ratings from Home.tsx (avoids duplicate API call) */
  ratings: EloRating[];
  /** Most recent match (first element from listMatches) */
  lastMatch: Match | null;
}

const GymnasiumSpotlight: React.FC<GymnasiumSpotlightProps> = ({ ratings, lastMatch }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // Collapse if no ranked personas — Home.tsx conditionally renders this
  if (ratings.length === 0) return null;

  const entries = ratings.map(r => ({ id: r.persona_id, name: r.name, value: r.elo }));
  const winner =
    lastMatch?.verdict
      ? lastMatch.contestants.find(c => c.persona_id === lastMatch.verdict!.winner)
      : null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
        Gymnasium Spotlight
      </h2>

      <MiniLeaderboard
        entries={entries}
        limit={5}
        valueLabel="Elo"
        accentColor="amber"
        onEntryClick={() => navigate('/gymnasium/rankings')}
      />

      {lastMatch && winner && (
        <div
          className={`rounded-lg px-3 py-2 flex items-center gap-2 ${
            darkMode ? 'bg-gray-800/50' : 'bg-gray-50'
          }`}
        >
          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last match:</span>
          <PersonaBadge
            personaId={winner.persona_id}
            personaName={winner.persona_name}
            size="xs"
            clickable={false}
          />
          <span className={`text-xs font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>won</span>
        </div>
      )}

      <button
        onClick={() => navigate('/gymnasium')}
        className={`w-full text-xs py-1.5 rounded-lg flex items-center justify-center gap-1 transition-colors ${
          darkMode ? 'text-amber-400 hover:bg-amber-900/20' : 'text-amber-600 hover:bg-amber-50'
        }`}
      >
        Enter Gymnasium <FiArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
};

export default GymnasiumSpotlight;
